import React from "react";

export interface Booking {
  title?: string;
  equipment?: string;
  start: Date;
  end: Date;
  deliveryMethod?: string;
  deliveryAddress?: string;
}

interface TodayLogisticsProps {
  bookings: Booking[];
}

const TodayLogistics: React.FC<TodayLogisticsProps> = ({ bookings = [] }) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const normalize = (str?: string) => (str || "").trim().toLowerCase();

  const deliveries = bookings.filter((b) => {
    if (!b?.start) return false;
    const start = new Date(b.start);
    start.setHours(0, 0, 0, 0);

    return (
      ["vi leverer og henter", "vi leverer", "tur/retur"].includes(
        normalize(b.deliveryMethod)
      ) && start.getTime() === today.getTime()
    );
  });

  const pickups = bookings.filter((b) => {
    if (!b?.end) return false;
    const end = new Date(b.end);
    end.setHours(0, 0, 0, 0);

    return (
      ["vi leverer og henter", "vi henter", "tur/retur"].includes(
        normalize(b.deliveryMethod)
      ) && end.getTime() === today.getTime()
    );
  });

  if (deliveries.length === 0 && pickups.length === 0) {
    return (
      <div className="bg-gray-50 p-4 rounded-lg border">
        <h2 className="text-lg font-semibold mb-2 text-gray-700">
          Dagens logistikk
        </h2>
        <p className="text-gray-500 text-sm">
          Ingen leveringer eller hentinger planlagt i dag.
        </p>
      </div>
    );
  }

  return (
    <div className="bg-white p-4 rounded-lg border shadow-sm">
      <h2 className="text-lg font-semibold mb-4 text-gray-800">
        Dagens logistikk
      </h2>

      {deliveries.length > 0 && (
        <div className="mb-6">
          <h3 className="text-green-700 font-semibold mb-3 flex items-center">
            <span className="mr-2">🚚</span>
            Leveringer i dag ({deliveries.length})
          </h3>
          <div className="space-y-2">
            {deliveries.map((booking, index) => (
              <div
                key={index}
                className="bg-green-50 p-3 rounded border-l-4 border-green-400"
              >
                <div className="font-medium text-green-800">
                  {booking.equipment || booking.title || "Ukjent utstyr"}
                </div>
                <div className="text-sm text-green-600">
                  Til: {booking.deliveryAddress || "Ukjent adresse"}
                </div>
                <div className="text-xs text-green-500 mt-1">
                  Metode: {booking.deliveryMethod || "Ikke spesifisert"}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {pickups.length > 0 && (
        <div>
          <h3 className="text-red-700 font-semibold mb-3 flex items-center">
            <span className="mr-2">📦</span>
            Hentinger i dag ({pickups.length})
          </h3>
          <div className="space-y-2">
            {pickups.map((booking, index) => (
              <div
                key={index}
                className="bg-red-50 p-3 rounded border-l-4 border-red-400"
              >
                <div className="font-medium text-red-800">
                  {booking.equipment || booking.title || "Ukjent utstyr"}
                </div>
                <div className="text-sm text-red-600">
                  Fra: {booking.deliveryAddress || "Ukjent adresse"}
                </div>
                <div className="text-xs text-red-500 mt-1">
                  Metode: {booking.deliveryMethod || "Ikke spesifisert"}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default TodayLogistics;
